/**
 * PurchaseOrder(進貨單)zod schema —— 前後端共用(REQ-20260806002)。
 *
 * 進貨類別(orderType)原本是寫死的兩種:紙張採購(PAPER)與發外包加工
 * (OUTSOURCE,燙金 / 軋型…)。兩者欄位完全相同,差別在於 OUTSOURCE 通常會綁一個
 * 代管客戶 —— 過帳時「進貨數量 > 已出貨數量」的部分會拋轉成該客戶的代管庫存。
 *
 * REQ-20260814002 起**類別清單改由設定頁的 `purchase_categories` 管理**,
 * 中文對照不再寫死在這裡(原本的 `PURCHASE_ORDER_TYPES` 已移除)——
 * 前端一律 `useReferences('purchase-categories')` 拿 code → name。
 *
 * REQ-20260818002 起這張單同時承接紙本的**派工單**(發包給代工廠的生產指示單)與
 * **訂購單 / 紙品採購單**:兩者在會計意義上都是「廠商供貨或供工 → 產生應付」,
 * 所以是同一張單的子類型而不是新的單據體系。派工類多出來的生產指示欄位
 * (印刷顏色 / 印工張數 / 成品尺寸…)全部 optional,一般訂購單留空即可。
 */
import { z } from 'zod';
import { decimalString, nonNegativeDecimalString, positiveDecimalString } from './decimal.schema';

// ── 進貨入帳(REQ-20260813001)的選單常數 ──────────────────────────────
// 稅別(PURCHASE_TAX_CATEGORIES)定義在 utils/accounting.ts,與計算公式放一起。

/** 單據狀態的中文對照。流程順序 DRAFT → PENDING_CONFIRM → POSTED,與 enum 宣告順序無關。 */
export const PURCHASE_ORDER_STATUSES = {
  DRAFT: '草稿',
  PENDING_CONFIRM: '待確認',
  POSTED: '已確認',
  VOIDED: '已作廢',
  HISTORICAL: '歷史單',
} as const;

export const PURCHASE_STOCK_IN_TYPES = {
  OWNED: '自有主倉',
  CONSIGNED: '代管主倉',
  DIRECT_SHIP: '不入庫直送',
} as const;
export type PurchaseStockInType = keyof typeof PURCHASE_STOCK_IN_TYPES;

export const PURCHASE_PAYMENT_METHODS = {
  CASH: '現金',
  TRANSFER: '匯款',
  CHECK: '支票',
  MONTHLY: '月結',
} as const;
export type PurchasePaymentMethod = keyof typeof PURCHASE_PAYMENT_METHODS;

export const PURCHASE_GRAIN_DIRECTIONS = {
  VERTICAL: '直紋',
  HORIZONTAL: '橫紋',
  NONE: '不分',
} as const;
export type PurchaseGrainDirection = keyof typeof PURCHASE_GRAIN_DIRECTIONS;

export const PURCHASE_COST_DESTINATIONS = {
  TO_STOCK: '進庫存',
  DIRECT_TO_WORK_ORDER: '直接進工單',
} as const;
export type PurchaseCostDestination = keyof typeof PURCHASE_COST_DESTINATIONS;

/**
 * 運費怎麼結(REQ-20260907002)—— **只有運輸費用類的進貨單填得到**,單頭一格四選一。
 *
 * ⚠️ 與 REQ-20260813003 移除的那個「付款方式」(`paymentMethod`,現金 / 匯款 / 支票 /
 * 月結)**不是同一件事**,所以是新欄位而不是把那一格叫回來:
 *  - `paymentMethod` 問的是「用什麼工具付」,對所有進貨單都成立,那次是使用者
 *    指名刪掉整個「入庫與付款」fieldset(連入庫類型一起),值改由後端推算。
 *  - `freightPaymentMode` 問的是「這筆運費要不要變成應付」——`COLLECT` 根本不是
 *    我方的錢,`CASH` 當場付掉,`MONTHLY` 才進應付。它會**改變金額歸屬**,
 *    而且只在運費類出現。
 *
 * `PER_LINE`(逐筆指定)時每一列各自帶 `linePaymentType`;其餘三種時所有列
 * 跟著單頭走(前端送出時已解析成逐列的值,見 REQ-20260907002 §1.2)。
 */
export const FREIGHT_PAYMENT_MODES = {
  MONTHLY: '月結應付',
  CASH: '當場付現',
  COLLECT: '到付 · 客戶付',
  PER_LINE: '逐筆指定',
} as const;
export type FreightPaymentMode = keyof typeof FREIGHT_PAYMENT_MODES;

/**
 * 成本歸屬(REQ-20260907002)。費用類進貨不進庫存,必須指定去向才能結案。
 *
 * ⚠️ 本次**只做記錄與顯示,不做金額分攤計算** —— `SPREAD_WORK_ORDERS`
 * (整單分攤到多張工單)的分攤比例規則使用者沒給(按金額?按件數?按重量?),
 * 猜一個寫進去等於偽造成本。欄位先落地讓資料收得起來,算法待使用者確認。
 */
export const PURCHASE_COST_ALLOCATION_MODES = {
  BY_LINE: '依明細各自歸屬',
  SPREAD_WORK_ORDERS: '整單分攤到多張工單',
  OPERATING_EXPENSE: '計入營業費用',
} as const;
export type PurchaseCostAllocationMode = keyof typeof PURCHASE_COST_ALLOCATION_MODES;

/**
 * 現付來源的常用選項(REQ-20260907002)。**只是建議不是值域** ——
 * 清單外的來源照樣從挑選框下面的手動輸入框打得進去,態度同 `UNIT_NAME_OPTIONS`。
 *
 * ⚠️ 正確的來源清單**待使用者確認**:公司實際的零用金 / 代墊科目怎麼分,
 * 派工時沒有說。這三個是從附圖的「零用金」外推的最小集合。
 */
export const FREIGHT_CASH_SOURCE_OPTIONS = ['零用金', '公司現金', '個人代墊'] as const;

/**
 * 尺寸 / 開數的建議選項。**不是 DB enum、也不做成 reference 表**:
 * 使用者填得進任何字串;做成 enum 反而擋掉現場真實會遇到的規格。
 *
 * REQ-20260902003 起語意收斂成**「訂購大張原紙(規格)後,指定裁修完成交付的開數」**,
 * 清單也整份換成使用者指名的常用開數 —— 原本那幾個(菊全 / 四六全 / 菊八開 /
 * 四六八開)講的是**大紙規格**,與「規格」那一格重複;大紙規格改由該格的建議清單
 * (四六版 / 菊版)負責。
 *
 * 「其他」不再放進清單:存進 DB 的字面「其他」沒有任何資訊,
 * 清單外的開數改由挑選框自己的手動輸入框填。
 *
 * ⚠️ **正式資料裡仍有上述舊值**,而且 sizeSpec 同時是派工單列印頁的「紙張尺寸」——
 * 所以這只是建議清單,前端不能用封閉的 `<select>` 呈現(見 REQ-20260902003)。
 */
export const PURCHASE_SIZE_SPECS = [
  'G4K',
  'G8K',
  '5K',
  '8K',
  '9K',
  '10K',
  '12K',
  '12K(信封)',
  '15K',
] as const;

/** 附件:jpg / png / pdf,單檔 10MB。前後端共用同一組限制。 */
export const PURCHASE_ATTACHMENT_ALLOWED_MIMES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
] as const;
export const PURCHASE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD');
const taxCategoryEnum = z.enum(['TAXABLE', 'ZERO_RATED', 'EXEMPT']);

export const PurchaseOrderLineInputSchema = z.object({
  productId: z.number().int().positive().optional().nullable(),
  productCode: z.string().trim().max(20).default('MANUAL'),
  productName: z.string().trim().min(1, '品名必填').max(60),
  productSpec: z.string().trim().max(60).optional().nullable(),
  /**
   * 材質描述(REQ-20260904003)。「商品採購」類的品項才填 —— 進的是成品而不是紙,
   * 紙張規格那一組整組不顯示,材質只能靠這一格記。
   * 命名與長度**逐字比照出貨單的 `productMaterial`**,不另創新名字。
   * null = 未填(既有品項一律如此,不回填)。
   */
  productMaterial: z.string().trim().max(60).optional().nullable(),
  /** 採購單位(進貨時論的單位);庫存單位數量由 conversionRate 換算 */
  unitName: z.string().trim().max(30).optional().nullable(),
  qty: positiveDecimalString,
  unitPrice: nonNegativeDecimalString,
  discount: nonNegativeDecimalString.optional(),
  /**
   * 紙張顏色(REQ-20260902003)。白 / 牛皮 / 米黃這類**紙本身**的顏色,
   * 與派工用的 `printColor`(印上去的顏色)是兩個欄位,不共用。
   */
  paperColor: z.string().trim().max(30).optional().nullable(),
  /** 磅數(g/m²)。紙張採購才有意義,後端不強制擋 —— 見 schema.prisma 的註解 */
  weightGsm: z.number().int().positive().max(9999).optional().nullable(),
  /**
   * 厚度 / 磅數的單位(REQ-20260902003):gsm/g、條、P、OZ、um/μm…
   * 存的就是使用者選到 / 打進去的字串,**後端不做任何換算**。
   * null = 未指定(既有品項一律如此,不回填)。
   */
  weightUnit: z.string().trim().max(10).optional().nullable(),
  sizeSpec: z.string().trim().max(30).optional().nullable(),
  grainDirection: z.enum(['VERTICAL', 'HORIZONTAL', 'NONE']).optional().nullable(),
  /** 1 採購單位 = 幾個庫存單位。沒填視為 1 */
  conversionRate: positiveDecimalString.optional().nullable(),
  /** null = 沿用單頭稅別(不是「沒有稅別」) */
  taxCategory: taxCategoryEnum.optional().nullable(),
  /**
   * 工單 / 案號,**可複選多張出貨單號**(REQ-20260905003)。
   * 一令紙的採購價與拆成兩次各半令不同,所以品項不為了「這令給哪幾張單用」而拆列 ——
   * 一項掛多個案號,金額 / 數量不受影響。
   *
   * 每個元素仍然是**純單號**(`max(12)`,對得回 `sales_orders.orderNo`)——
   * 顯示層的「單號 - 品名 等N項」在前端反查,不會存進來(REQ-20260905001 §一.3)。
   * 沒挑就是空陣列,不留 null 這第二種「沒有」。
   * `max(10)` 純粹是防呆(擋掉腳本 / 貼上一整頁的意外),不是業務規則 ——
   * 現場一令紙分給十張以上的訂單並不合理,真的遇到再放寬。
   */
  workOrderNos: z.array(z.string().trim().max(12)).max(10).optional().default([]),
  costDestination: z.enum(['TO_STOCK', 'DIRECT_TO_WORK_ORDER']).default('TO_STOCK'),
  notes: z.string().trim().max(200).optional().nullable(),
  // ── 派工單的生產指示(REQ-20260818002)。只有「派工」類的單會填,
  //    一般訂購單一律留空 —— 全部 optional,舊 client 的 payload 仍然合法。
  //    紙本的其餘格子沿用既有欄位:精樣名稱 = productName、印刷用紙 = productSpec、
  //    紙張尺寸 = sizeSpec、印刷數量 = qty。
  /** 印刷顏色。建議清單來自 print_color_options,仍可手填(不是值域) */
  printColor: z.string().trim().max(60).optional().nullable(),
  specialItems: z.string().trim().max(200).optional().nullable(),
  colorProofRequired: z.boolean().default(false),
  /** 印工張數 —— 與印刷數量(qty)不同:同一張紙可拼版印多個成品 */
  impressionQty: z.number().int().nonnegative().max(99_999_999).optional().nullable(),
  finishedSize: z.string().trim().max(60).optional().nullable(),
  finishedQty: z.number().int().nonnegative().max(99_999_999).optional().nullable(),
  /** 加工項目 / 後製加工。自由文字 —— 紙本寫的是「打孔:1 孔孔徑 3mm」這種帶細節的句子 */
  postProcessing: z.string().trim().max(200).optional().nullable(),
  /**
   * 委外加工 / 派工完成後,廠商實際交回(或完成)的數量(REQ-20260821006)。
   * 精度比照 `qty`(Decimal(14,4))—— 兩個數字要能直接相減看耗損。
   * 可能**少於** qty(耗損:送 1100 交回 1060)也可能**多於** qty(溢收);
   * null = 尚未回報。**純記錄**:不參與金額(`calcPurchaseTotals`)、
   * 庫存單位數量(`calcStockUnitQty`)或代管庫存拋轉的任何計算。
   */
  completedQty: nonNegativeDecimalString.optional().nullable(),
  // ── 運輸費用單的明細欄位(REQ-20260907002)。只有運費類填得到,其餘一律 null。
  //    全部 optional,舊 client 的 payload 仍然合法;既有品項一律 NULL 不回填。
  /** 運送日。與單頭的帳單日期(txnDate)各自獨立 —— 一張月結對帳單涵蓋整個月的班次 */
  shipDate: dateOnly.optional().nullable(),
  /** 託運單號(貨運行給的)。純文字不設 unique:重開單 / 補號的情況下 unique 會把單擋住 */
  trackingNo: z.string().trim().max(30).optional().nullable(),
  /**
   * 送達客戶。**這裡設 FK(見 migration 0047)** —— 與工單 / 案號那一格刻意不同:
   * 案號可能是「單還沒開先叫料」的口頭號碼,客戶則一定是主檔裡已經有的人。
   */
  deliveryCustomerId: z.number().int().positive().optional().nullable(),
  /**
   * 這一列的付款方式。單頭選「逐筆指定」時逐列可改,其餘三種時前端一律送單頭的值。
   * ⚠️ `'COLLECT'`(到付)**會把整列排除在單據金額之外**(見 `calcPurchaseTotals()`)。
   */
  linePaymentType: z.enum(['MONTHLY', 'CASH', 'COLLECT']).optional().nullable(),
  /**
   * 這一列的運費用哪一種方式分攤到多張工單(REQ-20260908002):
   * BY_AMOUNT 依品項金額比例 / EVEN 平均分攤 / MANUAL 手動指定。
   * null = 沒有分攤過(只有一張工單、或還沒處理)。
   */
  allocationMethod: z.enum(['BY_AMOUNT', 'EVEN', 'MANUAL']).optional().nullable(),
  /**
   * 分攤結果:每一張工單分到多少(REQ-20260908002)。
   *
   * ⚠️ **不是第二份「這一列有哪些工單」的清單** —— 工單清單仍然只有
   * `workOrderNos` 一份,這裡只是**依單號帶金額**。後端寫入時以 `workOrderNos`
   * 為準,對不到的單號直接忽略(前端換過工單但沒重算分攤時,寧可留空也不要
   * 憑空生出一列)。金額由前後端共用的 `allocateFreight()` 算,不各算一次。
   */
  workOrderAllocations: z
    .array(z.object({ workOrderNo: z.string().trim().max(12), amount: nonNegativeDecimalString }))
    .max(10)
    .optional(),
});
export type PurchaseOrderLineInput = z.infer<typeof PurchaseOrderLineInputSchema>;

export const CreatePurchaseOrderSchema = z.object({
  supplierId: z.number().int().positive(),
  /**
   * 進貨類別的 code。**不是 enum**(REQ-20260814002):使用者可以在設定頁自己加第三個
   * 類別,寫死成 enum 的話新增完反而建不了單。長度對齊 DB 的 VARCHAR(20) 與
   * reference 的 code 上限;`PAPER` 是 DB 欄位本身的預設值,沒帶就落在它。
   */
  orderType: z.string().trim().min(1).max(20).default('PAPER'),
  /** 代管客戶:發外包加工時填,決定代管庫存拋轉給誰 */
  customerId: z.number().int().positive().optional().nullable(),
  /**
   * 用紙廠商(REQ-20260818002 派工單)——「代工單位」(supplierId)之外的**另一家**廠商:
   * 紙由這家供、工由代工單位做。**純顯示用,不產生第二筆應付**:
   * 這張單的金額仍然全數掛在 supplierId 名下(要各自結帳就開兩張單)。
   */
  paperSupplierId: z.number().int().positive().optional().nullable(),
  /** 取件 / 交件方式(自由文字):完成通知自取 / 送至某某單位 / 通知某某單位取件 */
  pickupMethod: z.string().trim().max(100).optional().nullable(),
  /** 來源訪談:拋轉代管庫存時,商品圖片從這筆訪談的第一張圖片附件複製 */
  interviewId: z.number().int().positive().optional().nullable(),
  txnDate: dateOnly,
  dueDate: dateOnly.optional().nullable(),
  /**
   * 實際到貨入庫日(REQ-20260916001)。**選填** —— 下訂當下不知道貨哪天到,到貨後回來補。
   *
   * ⚠️ 與 `txnDate` 不是同一件事:`txnDate` 是**單據日期**(跟廠商下訂 / 開單那天),
   * 決定單號的年月日、應付月份與預計付款日;這一格純粹記「東西哪天真的到」,
   * 不推算任何東西、也不影響報表的認列基準。沒填就是沒填,後端不帶預設值。
   */
  receivedDate: dateOnly.optional().nullable(),
  receivingAddress: z.string().trim().max(255).optional().nullable(),
  /** 廠商發票號碼。同廠商同號碼(排除作廢單)只能出現一次,後端擋 */
  invoiceNo: z.string().trim().max(20).optional().nullable(),
  /** 未填時後端預設帶入 txnDate;之後兩者各自獨立 */
  invoiceDate: dateOnly.optional().nullable(),
  taxCategory: taxCategoryEnum.default('TAXABLE'),
  unitPriceTaxInclusive: z.boolean().default(false),
  stockInType: z.enum(['OWNED', 'CONSIGNED', 'DIRECT_SHIP']).default('OWNED'),
  paymentMethod: z.enum(['CASH', 'TRANSFER', 'CHECK', 'MONTHLY']).optional().nullable(),
  /** 未填時後端依廠商付款條件推算 */
  expectedPaymentDate: dateOnly.optional().nullable(),
  /** 覆寫系統推算的預計付款日時必填 */
  expectedPaymentDateReason: z.string().trim().max(200).optional().nullable(),
  taxRate: decimalString.optional(),
  notes: z.string().trim().max(2000).optional().nullable(),
  // ── 運輸費用單的單頭欄位(REQ-20260907002)。只有運費類填得到,其餘一律 null ──
  /** 廠商月結單號(對帳單號)。選填 —— 廠商還沒寄對帳單就先建單是常態 */
  statementNo: z.string().trim().max(30).optional().nullable(),
  /** 運費怎麼結:月結應付 / 當場付現 / 到付 · 客戶付 / 逐筆指定 */
  freightPaymentMode: z.enum(['MONTHLY', 'CASH', 'COLLECT', 'PER_LINE']).optional().nullable(),
  /** 現付來源(零用金 / 公司現金 / 個人代墊…)。建議清單見 `FREIGHT_CASH_SOURCE_OPTIONS` */
  cashSource: z.string().trim().max(30).optional().nullable(),
  /** 付款人。附圖小字寫「由現場記錄自動帶入」,但那個資料來源目前不存在 → 先做自由文字 */
  payerName: z.string().trim().max(50).optional().nullable(),
  /** 成本歸屬。本次只做記錄與顯示,不做金額分攤計算 */
  costAllocationMode: z
    .enum(['BY_LINE', 'SPREAD_WORK_ORDERS', 'OPERATING_EXPENSE'])
    .optional()
    .nullable(),
  lines: z.array(PurchaseOrderLineInputSchema).min(1, '至少要有一個品項'),
});
export type CreatePurchaseOrderInput = z.infer<typeof CreatePurchaseOrderSchema>;

export const UpdatePurchaseOrderSchema = CreatePurchaseOrderSchema.omit({ supplierId: true });
export type UpdatePurchaseOrderInput = z.infer<typeof UpdatePurchaseOrderSchema>;

export const PurchaseOrderAllowanceSchema = z.object({
  allowanceAmount: nonNegativeDecimalString,
  reason: z.string().trim().max(200).optional().nullable(),
});
export type PurchaseOrderAllowanceInput = z.infer<typeof PurchaseOrderAllowanceSchema>;

/** 退回草稿(PENDING_CONFIRM → DRAFT):要留下退回理由,否則製單人不知道該改什麼 */
export const ReturnPurchaseOrderToDraftSchema = z.object({
  reason: z.string().trim().min(1, '請填寫退回原因').max(200),
});
export type ReturnPurchaseOrderToDraftInput = z.infer<typeof ReturnPurchaseOrderToDraftSchema>;

// ── 附件(REQ-20260813001)──────────────────────────────────────────────

export const PresignPurchaseAttachmentSchema = z.object({
  originalName: z.string().min(1).max(255),
  mimeType: z.enum(PURCHASE_ATTACHMENT_ALLOWED_MIMES),
  sizeBytes: z.number().int().positive().max(PURCHASE_ATTACHMENT_MAX_BYTES),
});
export type PresignPurchaseAttachmentInput = z.infer<typeof PresignPurchaseAttachmentSchema>;

export const CreatePurchaseAttachmentSchema = PresignPurchaseAttachmentSchema.extend({
  storedName: z.string().min(1),
  storagePath: z.string().min(1),
});
export type CreatePurchaseAttachmentInput = z.infer<typeof CreatePurchaseAttachmentSchema>;

export interface PurchaseOrderAttachmentResponse {
  id: number;
  uuid: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface PresignPurchaseUploadResponse {
  uploadUrl: string;
  storedName: string;
  storagePath: string;
  expiresInSec: number;
}

export interface PurchaseOrderListItem {
  id: number;
  uuid: string;
  orderNo: string;
  status: string;
  source: string;
  orderType: string;
  txnDate: string;
  dueDate: string | null;
  /** 實際到貨入庫日(REQ-20260916001)。沒補填過的單(含所有既有單)一律 null */
  receivedDate: string | null;
  supplierId: number;
  supplierSimpleName: string;
  supplierFullName: string;
  customerId: number | null;
  customerSimpleName: string | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  taxCategory: string;
  unitPriceTaxInclusive: boolean;
  stockInType: string;
  paymentMethod: string | null;
  expectedPaymentDate: string | null;
  expectedPaymentDateReason: string | null;
  /** 對帳單號(REQ-20260907002)。運費類才填,其餘一律 null */
  statementNo: string | null;
  subtotal: string;
  tax: string;
  total: string;
  allowanceAmount: string | null;
  settledAmount: string;
  outstanding: string;
  lineCount: number;
}

export interface PurchaseOrderDetail extends PurchaseOrderListItem {
  receivingAddress: string | null;
  /**
   * 用紙廠商(REQ-20260818002)。只放在明細不放清單:列表沒有這一欄,
   * 多 join 一張表只為了不顯示的值不划算。名稱兩份的理由同 supplier ——
   * 表單顯示簡稱、列印頁印全名。
   */
  paperSupplierId: number | null;
  paperSupplierSimpleName: string | null;
  paperSupplierFullName: string | null;
  pickupMethod: string | null;
  notes: string | null;
  taxRate: string;
  // ── 運輸費用單的單頭欄位(REQ-20260907002)。運費類才填,其餘一律 null ──
  freightPaymentMode: string | null;
  cashSource: string | null;
  payerName: string | null;
  costAllocationMode: string | null;
  interviewId: number | null;
  submittedAt: string | null;
  /** ⚠️ 就是「確認」的時間 —— 底層仍是 postedAt(見 schema.prisma 的註解) */
  postedAt: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
  /** 過帳時拋轉出去的代管庫存筆數(0 = 沒有超額,或還沒過帳) */
  managedInventoryCount: number;
  attachments: PurchaseOrderAttachmentResponse[];
  lines: Array<{
    id: number;
    lineNo: number;
    productId: number | null;
    productCode: string;
    productName: string;
    productSpec: string | null;
    /** 材質描述(REQ-20260904003)。商品採購類才填,既有品項一律 null */
    productMaterial: string | null;
    unitName: string | null;
    qty: string;
    unitPrice: string;
    discount: string;
    amount: string;
    /** 紙張本身的顏色(REQ-20260902003)。與 printColor(印刷顏色)是兩回事 */
    paperColor: string | null;
    weightGsm: number | null;
    /** 厚度 / 磅數的單位(REQ-20260902003)。null = 未指定,不是 'gsm' */
    weightUnit: string | null;
    sizeSpec: string | null;
    grainDirection: string | null;
    conversionRate: string | null;
    stockUnitQty: string | null;
    taxCategory: string | null;
    /**
     * 工單 / 案號(REQ-20260905003)。一項可對應多張出貨單號,順序 = 當初的勾選順序。
     * 沒挑就是空陣列 —— 不是 null。舊單的單一案號由 migration 0046 搬進來。
     */
    workOrderNos: string[];
    /** 逐張工單分到多少(REQ-20260908002)。沒分攤過的一律空陣列 */
    workOrderAllocations: Array<{ workOrderNo: string; amount: string | null }>;
    /** 'BY_AMOUNT' | 'EVEN' | 'MANUAL';null = 沒有分攤過 */
    allocationMethod: string | null;
    costDestination: string;
    notes: string | null;
    // 派工單的生產指示(REQ-20260818002);非派工類的單一律 null / false
    printColor: string | null;
    specialItems: string | null;
    colorProofRequired: boolean;
    impressionQty: number | null;
    finishedSize: string | null;
    finishedQty: number | null;
    postProcessing: string | null;
    /** 廠商實際交回 / 完成的數量(REQ-20260821006)。null = 尚未回報 */
    completedQty: string | null;
    // ── 運輸費用單的明細欄位(REQ-20260907002)。運費類才填,其餘一律 null ──
    shipDate: string | null;
    trackingNo: string | null;
    deliveryCustomerId: number | null;
    /** 送達客戶的簡稱快照(給畫面顯示用,不另外打一支 API) */
    deliveryCustomerSimpleName: string | null;
    /** 'MONTHLY' | 'CASH' | 'COLLECT';'COLLECT' 的列不計入單據金額 */
    linePaymentType: string | null;
    // ── 運費歸單的來源軌跡(REQ-20260908002)。手動補登的列一律 null ──
    freightLineId: number | null;
    /**
     * 來源出貨所屬**訂單**的 id / 單號(B 字軌)。
     * ⚠️ `shipments` **沒有自己的單號**,所以「來源出貨單」顯示的是它所屬的那張訂單;
     * 而 `shipments.orderId` 是單一 FK,一筆出貨永遠只對應一張訂單。
     */
    freightSourceOrderId: number | null;
    freightSourceOrderNo: string | null;
  }>;
}

export interface PurchaseOrderListResponse {
  items: PurchaseOrderListItem[];
  total: number;
}

/** 過帳結果:告訴使用者有沒有拋轉代管庫存,以及拋了哪幾筆 */
export interface PostPurchaseOrderResponse {
  orderId: number;
  orderNo: string;
  status: string;
  managedInventory: Array<{
    inventoryRecordId: number;
    itemName: string;
    quantity: string;
    unit: string | null;
    /** 圖片有沒有一起帶過去 */
    hasImage: boolean;
  }>;
}
